/**
 * Data layer for `admin-api`. Thin typed fetch wrappers over /api/v1/admin-api.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Mirrors the shape of apps/api/src/routes/admin-api.ts. Scope codes are
 * duplicated here so the dash bundle does NOT pull in hono/drizzle during
 * the Vite transform.
 */

// --- Scopes (mirror of backend ADMIN_SCOPES) -------------------------------

export const ADMIN_SCOPES = [
	"campaigns.read",
	"campaigns.write",
	"publishers.read",
	"publishers.write",
	"conversions.read",
	"reports.read",
] as const;
export type AdminScope = (typeof ADMIN_SCOPES)[number];

// --- Types (mirror backend) ------------------------------------------------

export interface AdminEndpoint {
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	path: string;
	description: string;
	scope: AdminScope;
}

export interface ListEndpointsResponse {
	count: number;
	endpoints: AdminEndpoint[];
	scopes: AdminScope[];
}

export interface AdminApiKeyMasked {
	id: string;
	tenant_id: string;
	name: string;
	key_preview: string;
	scopes: AdminScope[];
	active: boolean;
	created_at: number;
	revoked_at: number | null;
}

export interface ListKeysResponse {
	tenant_id: string;
	count: number;
	keys: AdminApiKeyMasked[];
}

export interface CreateKeyBody {
	name: string;
	scopes: AdminScope[];
}

export interface CreateKeyResponse {
	key: AdminApiKeyMasked;
	/** ONE-TIME reveal — raw secret returned only on creation. */
	raw_key: string;
}

export interface RevokeKeyResponse {
	id: string;
	revoked_at: number;
	revoked: boolean;
}

export interface AccessLogRow {
	id: string;
	tenant_id: string;
	key_id: string | null;
	method: string;
	path: string;
	status_code: number;
	ts: number;
}

export interface ListAccessLogResponse {
	tenant_id: string;
	limit: number;
	count: number;
	entries: AccessLogRow[];
}

export interface RateLimitInfo {
	tenant_id: string;
	limit_per_minute: number;
	window_seconds: number;
	used_current_window: number;
	remaining: number;
}

// --- Fetch wrappers --------------------------------------------------------

const BASE = "/api/v1/admin-api";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listEndpoints(): Promise<ListEndpointsResponse> {
	const res = await fetch(`${BASE}/endpoints`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListEndpointsResponse>(res);
}

export async function listKeys(): Promise<ListKeysResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListKeysResponse>(res);
}

export async function createKey(body: CreateKeyBody): Promise<CreateKeyResponse> {
	const res = await fetch(`${BASE}/keys`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateKeyResponse>(res);
}

export async function revokeKey(id: string): Promise<RevokeKeyResponse> {
	const res = await fetch(`${BASE}/keys/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RevokeKeyResponse>(res);
}

export async function listAccessLog(limit?: number): Promise<ListAccessLogResponse> {
	const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : "";
	const res = await fetch(`${BASE}/access-log${qs}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAccessLogResponse>(res);
}

export async function getRateLimit(): Promise<RateLimitInfo> {
	const res = await fetch(`${BASE}/rate-limit`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<RateLimitInfo>(res);
}

// --- Query keys ------------------------------------------------------------

export const adminApiQueryKeys = {
	all: ["admin-api"] as const,
	endpoints: () => [...adminApiQueryKeys.all, "endpoints"] as const,
	keys: () => [...adminApiQueryKeys.all, "keys"] as const,
	accessLog: (limit?: number) =>
		[...adminApiQueryKeys.all, "access-log", limit ?? "default"] as const,
	rateLimit: () => [...adminApiQueryKeys.all, "rate-limit"] as const,
};
